import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { db, storage } from '../firebaseConfig';
import PageInfoButton from '../components/PageInfoButton';
import { formatSaveMessage, saveToGallery } from '../utils/saveToGallery';
import './Notes.css';

const EMPTY_NOTE = {
  title: '',
  narrative: ''
};

const CACHE_PREFIX = 'cachedNotes_';
const FETCH_PREFIX = 'lastNotesFetch_';

const timestampToMillis = (timestamp) => {
  if (!timestamp) return 0;
  if (typeof timestamp === 'string') return new Date(timestamp).getTime() || 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000;
  return 0;
};

const formatTimestamp = (timestamp) => {
  const millis = timestampToMillis(timestamp);
  return millis ? new Date(millis).toLocaleString() : '';
};

const sortNotesNewestFirst = (items) =>
  [...items].sort((a, b) => timestampToMillis(b.timestamp) - timestampToMillis(a.timestamp));

const highlightMatch = (text, searchTerm) => {
  if (!text || !searchTerm) return text || '';

  const lower = text.toLowerCase();
  const index = lower.indexOf(searchTerm.toLowerCase());

  if (index === -1) return text;

  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + searchTerm.length)}</mark>
      {text.slice(index + searchTerm.length)}
    </>
  );
};

export default function Notes({ sidebarVisible }) {
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [expandedNoteId, setExpandedNoteId] = useState(null);
  const [editModeId, setEditModeId] = useState(null);
  const [noteForm, setNoteForm] = useState(EMPTY_NOTE);
  const [editedNoteData, setEditedNoteData] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [userRank, setUserRank] = useState('');
  const [userAgency, setUserAgency] = useState('');
  const [userState, setUserState] = useState('');
  const [userId, setUserId] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [editedFiles, setEditedFiles] = useState([]);
  const [editedPreviewUrls, setEditedPreviewUrls] = useState([]);
  const [modalImage, setModalImage] = useState(null);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isRefreshingNotes, setIsRefreshingNotes] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState('');

  const cacheKey = userId ? `${CACHE_PREFIX}${userId}` : '';
  const fetchKey = userId ? `${FETCH_PREFIX}${userId}` : '';

  const saveNotesToCache = useCallback(
    (items) => {
      if (!cacheKey) return;
      localStorage.setItem(cacheKey, JSON.stringify(sortNotesNewestFirst(items)));
    },
    [cacheKey]
  );

  const fetchNotes = useCallback(async () => {
    if (!userId) return;

    setIsRefreshingNotes(true);
    try {
      const notesQuery = query(collection(db, 'notes'), where('ownerId', '==', userId));
      const snapshot = await getDocs(notesQuery);
      const userNotes = sortNotesNewestFirst(
        snapshot.docs.map((noteDoc) => ({ id: noteDoc.id, ...noteDoc.data() }))
      );

      setNotes(userNotes);
      saveNotesToCache(userNotes);
      localStorage.setItem(fetchKey, Date.now().toString());
    } catch (error) {
      console.error('Error fetching notes:', error);
      toast.error('Unable to refresh notes.');
    } finally {
      setIsRefreshingNotes(false);
    }
  }, [fetchKey, saveNotesToCache, userId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), async (currentUser) => {
      if (!currentUser) {
        setNotes([]);
        setUserId(null);
        setUserLoaded(true);
        return;
      }

      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const data = userSnap.data();
          setDisplayName(data.displayName || currentUser.email || '');
          setUserRank(data.rank || '');
          setUserAgency(data.agency || '');
          setUserState(data.state || '');
        } else {
          setDisplayName(currentUser.email || '');
        }

        setUserId(currentUser.uid);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setDisplayName(currentUser.email || '');
        setUserId(currentUser.uid);
      } finally {
        setUserLoaded(true);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!userId || !cacheKey) return;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const parsed = sortNotesNewestFirst(JSON.parse(cached));
        setNotes(parsed);
      } catch (error) {
        console.error('Failed to parse cached notes:', error);
        localStorage.removeItem(cacheKey);
        fetchNotes();
      }
      return;
    }

    setNotes([]);
    fetchNotes();
  }, [cacheKey, fetchNotes, userId]);

  useEffect(() => {
    if (!expandedNoteId || editModeId || modalVisible || modalImage) return undefined;

    const handleOutsidePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (
        target.closest('.note-preview.expanded') ||
        target.closest('.note-modal') ||
        target.closest('.image-preview-overlay') ||
        target.closest('.add-note-btn')
      ) {
        return;
      }

      setExpandedNoteId(null);
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [editModeId, expandedNoteId, modalImage, modalVisible]);

  const filteredNotes = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    if (!searchTerm) return notes;

    return notes.filter((note) => (note.title || '').toLowerCase().includes(searchTerm));
  }, [notes, search]);

  const uploadNotePhotos = async (files) => {
    if (!files.length) return [];

    const uploaded = await Promise.all(
      files.map(async (file) => {
        const storageRef = ref(storage, `notes/${userId}/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        return getDownloadURL(snapshot.ref);
      })
    );

    return uploaded;
  };

  const resetCreateForm = () => {
    setNoteForm(EMPTY_NOTE);
    setSelectedFiles([]);
    setPreviewUrls([]);
    setModalVisible(false);
  };

  const handleAddNote = async () => {
    if (isCreatingNote) return;

    const title = noteForm.title.trim();
    const narrative = noteForm.narrative.trim();

    if (!userLoaded || !userId) {
      alert('Please wait for your profile to load before saving.');
      return;
    }

    if (!title || !narrative) {
      alert('Please enter a title and narrative before saving.');
      return;
    }

    setIsCreatingNote(true);
    try {
      const photoUrls = await uploadNotePhotos(selectedFiles);
      const fullNote = {
        title,
        narrative,
        type: 'Note',
        timestamp: new Date(),
        ownerId: userId,
        ownerName: displayName,
        rank: userRank,
        agency: userAgency,
        entryState: userState || '',
        flags: [],
        photoUrls
      };

      const docRef = await addDoc(collection(db, 'notes'), fullNote);
      const savedNote = { ...fullNote, id: docRef.id };
      const updatedNotes = sortNotesNewestFirst([savedNote, ...notes]);

      setNotes(updatedNotes);
      saveNotesToCache(updatedNotes);
      resetCreateForm();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to save note.');
    } finally {
      setIsCreatingNote(false);
    }
  };

  const handleEditNote = (note) => {
    setEditModeId(note.id);
    setEditedNoteData({ ...note });
    setEditedFiles([]);
    setEditedPreviewUrls([]);
  };

  const handleSaveInlineEdit = async () => {
    if (isSavingNote || !editedNoteData) return;

    const title = (editedNoteData.title || '').trim();
    const narrative = (editedNoteData.narrative || '').trim();

    if (!title || !narrative) {
      alert('Please enter a title and narrative before saving.');
      return;
    }

    setIsSavingNote(true);
    try {
      const { id, ...data } = editedNoteData;
      const uploaded = await uploadNotePhotos(editedFiles);
      const updatedData = {
        ...data,
        title,
        narrative,
        type: 'Note',
        entryState: data.entryState || userState || '',
        flags: data.flags || [],
        photoUrls: [...(data.photoUrls || []), ...uploaded],
        editedAt: new Date()
      };

      await updateDoc(doc(db, 'notes', id), updatedData);

      const updatedNotes = sortNotesNewestFirst(
        notes.map((note) => (note.id === id ? { ...updatedData, id } : note))
      );
      setNotes(updatedNotes);
      saveNotesToCache(updatedNotes);
      setEditModeId(null);
      setEditedNoteData(null);
      setEditedFiles([]);
      setEditedPreviewUrls([]);
    } catch (error) {
      console.error('Error updating note:', error);
      toast.error('Failed to update note.');
    } finally {
      setIsSavingNote(false);
    }
  };

  const handleDeleteNote = async (id) => {
    if (deletingNoteId) return;
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    setDeletingNoteId(id);
    try {
      await deleteDoc(doc(db, 'notes', id));
      const updatedNotes = notes.filter((note) => note.id !== id);
      setNotes(updatedNotes);
      saveNotesToCache(updatedNotes);
      if (expandedNoteId === id) setExpandedNoteId(null);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note.');
    } finally {
      setDeletingNoteId('');
    }
  };

  const handleWebShareNote = async (note) => {
    const attachmentText = (note.photoUrls || []).length
      ? `\n\nAttachments:\n${note.photoUrls.join('\n')}`
      : '';
    const content = `${note.title}\n\n${note.narrative}${attachmentText}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: note.title,
          text: content
        });
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Sharing failed:', error);
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(content);
      toast.success('Note copied to clipboard.');
    } catch (error) {
      console.error('Clipboard write failed:', error);
      toast.error('Unable to copy the note.');
    }
  };

  const handleCreateFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
    setPreviewUrls((prev) => [...prev, ...files.map((file) => URL.createObjectURL(file))]);
    event.target.value = '';
  };

  const handleEditFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    setEditedFiles((prev) => [...prev, ...files]);
    setEditedPreviewUrls((prev) => [...prev, ...files.map((file) => URL.createObjectURL(file))]);
    event.target.value = '';
  };

  const removeCreatePreview = (index) => {
    setSelectedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
    setPreviewUrls((prev) => prev.filter((_, previewIndex) => previewIndex !== index));
  };

  const removeEditPreview = (index) => {
    setEditedFiles((prev) => prev.filter((_, fileIndex) => fileIndex !== index));
    setEditedPreviewUrls((prev) => prev.filter((_, previewIndex) => previewIndex !== index));
  };

  const removeExistingPhoto = (index) => {
    setEditedNoteData((prev) => ({
      ...prev,
      photoUrls: (prev.photoUrls || []).filter((_, photoIndex) => photoIndex !== index)
    }));
  };

  return (
    <div className={`notes-page ${!sidebarVisible ? 'compact' : ''}`}>
      <div className="page-info-header-block">
        <h2 className="notes-title">Notes</h2>
        <div className="page-info-button-wrapper">
          <PageInfoButton title="About Notes">
            <p>Create personal notes with a title, narrative, and optional image attachments.</p>
            <ul className="page-info-list">
              <li><strong>Private by default:</strong> Your notes are visible only to you unless you share them outside the app.</li>
              <li><strong>Local first:</strong> Notes are stored on this device for faster access and backed up in the cloud.</li>
              <li><strong>Search:</strong> The search bar filters notes by title.</li>
            </ul>
          </PageInfoButton>
        </div>
      </div>

      <p className="notes-description">
        Create and save notes for personal use. If you do not see your notes,{' '}
        <button
          type="button"
          className="notes-refresh-link"
          onClick={fetchNotes}
          disabled={isRefreshingNotes || !userId}
        >
          {isRefreshingNotes ? 'refreshing...' : 'press here'}
        </button>
        .
      </p>

      <div className="search-bar-container">
        <input
          type="text"
          className="search-bar"
          placeholder="Search by title..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      <div className={`notes-list ${!sidebarVisible ? 'compact' : ''}`}>
        {filteredNotes.map((note) => {
          const isExpanded = expandedNoteId === note.id;
          const isEditing = editModeId === note.id;
          const photoUrls = isEditing ? editedNoteData?.photoUrls || [] : note.photoUrls || [];

          return (
            <div
              key={note.id}
              className={`note-preview ${isExpanded ? 'expanded' : ''}`}
              onClick={() => {
                if (!isEditing) setExpandedNoteId(isExpanded ? null : note.id);
              }}
            >
              <div className="note-card-title-row">
                <strong className="note-card-title">{highlightMatch(note.title, search)}</strong>
                <span className="note-card-caret">{isExpanded ? '^' : 'v'}</span>
              </div>

              {isExpanded && (
                <div className="note-details" onClick={(event) => event.stopPropagation()}>
                  {isEditing ? (
                    <div className="note-edit-fields">
                      <label>
                        <span>Title</span>
                        <input
                          className="note-input"
                          value={editedNoteData.title || ''}
                          onChange={(event) =>
                            setEditedNoteData((prev) => ({ ...prev, title: event.target.value }))
                          }
                          maxLength={120}
                        />
                      </label>
                      <label>
                        <span>Narrative</span>
                        <textarea
                          className="note-textarea full-width"
                          value={editedNoteData.narrative || ''}
                          onChange={(event) =>
                            setEditedNoteData((prev) => ({ ...prev, narrative: event.target.value }))
                          }
                          rows={8}
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="note-clean-display">
                      <div className="note-narrative">{note.narrative}</div>
                    </div>
                  )}

                  {photoUrls.length > 0 && (
                    <div className="note-attachments">
                      <strong>Attachments</strong>
                      <div className="note-photo-grid">
                        {photoUrls.map((url, index) => (
                          <div className="note-photo-thumb" key={url}>
                            <button
                              type="button"
                              className="note-photo-button"
                              onClick={() => setModalImage(url)}
                            >
                              <img src={url} alt={`Attachment ${index + 1}`} />
                            </button>
                            {isEditing && (
                              <button
                                type="button"
                                className="remove-photo-btn"
                                onClick={() => removeExistingPhoto(index)}
                                aria-label="Remove attachment"
                              >
                                x
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {isEditing && (
                    <div className="note-attachments">
                      <label className="file-picker-label">
                        <span>Add Attachments</span>
                        <input type="file" accept="image/*" multiple onChange={handleEditFileChange} />
                      </label>
                      {editedPreviewUrls.length > 0 && (
                        <div className="note-photo-grid">
                          {editedPreviewUrls.map((src, index) => (
                            <div className="note-photo-thumb" key={src}>
                              <img src={src} alt={`New attachment ${index + 1}`} />
                              <button
                                type="button"
                                className="remove-photo-btn"
                                onClick={() => removeEditPreview(index)}
                                aria-label="Remove selected attachment"
                              >
                                x
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="note-meta">
                    {formatTimestamp(note.timestamp) && <span>Saved on: {formatTimestamp(note.timestamp)}</span>}
                    {formatTimestamp(note.editedAt) && <span>Edited: {formatTimestamp(note.editedAt)}</span>}
                  </div>

                  <div className="note-action-buttons">
                    {isEditing ? (
                      <>
                        <button className="save-btn" onClick={handleSaveInlineEdit} disabled={isSavingNote}>
                          {isSavingNote ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="cancel-btn"
                          disabled={isSavingNote}
                          onClick={() => {
                            setEditModeId(null);
                            setEditedNoteData(null);
                            setEditedFiles([]);
                            setEditedPreviewUrls([]);
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => handleEditNote(note)}>Edit</button>
                        <button type="button" onClick={() => handleWebShareNote(note)}>Share</button>
                        <button
                          type="button"
                          disabled={Boolean(deletingNoteId)}
                          onClick={() => handleDeleteNote(note.id)}
                        >
                          {deletingNoteId === note.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {filteredNotes.length === 0 && (
          <div className="notes-empty-state">
            {search.trim() ? 'No notes match that title.' : 'No notes saved yet.'}
          </div>
        )}
      </div>

      {!modalVisible && (
        <button className="add-note-btn" onClick={() => setModalVisible(true)}>
          + Add Note
        </button>
      )}

      {modalVisible && (
        <div className="note-modal" role="dialog" aria-modal="true" aria-label="Add note">
          <div className="note-form">
            <h3>Add Note</h3>
            <label>
              <span>Title</span>
              <input
                className="note-input"
                type="text"
                placeholder="Title"
                value={noteForm.title}
                onChange={(event) => setNoteForm((prev) => ({ ...prev, title: event.target.value }))}
                maxLength={120}
              />
            </label>
            <label>
              <span>Narrative</span>
              <textarea
                className="note-textarea full-width"
                placeholder="Narrative"
                rows={8}
                value={noteForm.narrative}
                onChange={(event) => setNoteForm((prev) => ({ ...prev, narrative: event.target.value }))}
              />
            </label>

            <div className="note-attachments">
              <label className="file-picker-label">
                <span>Attach Images</span>
                <input type="file" accept="image/*" multiple onChange={handleCreateFileChange} />
              </label>
              {previewUrls.length > 0 && (
                <div className="note-photo-grid">
                  {previewUrls.map((src, index) => (
                    <div className="note-photo-thumb" key={src}>
                      <img src={src} alt={`Selected attachment ${index + 1}`} />
                      <button
                        type="button"
                        className="remove-photo-btn"
                        onClick={() => removeCreatePreview(index)}
                        aria-label="Remove selected attachment"
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="note-action-buttons">
              <button className="save-btn" onClick={handleAddNote} disabled={isCreatingNote}>
                {isCreatingNote ? 'Saving...' : 'Save'}
              </button>
              <button className="cancel-btn" onClick={resetCreateForm} disabled={isCreatingNote}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="notes-bottom-spacer" />

      {modalImage && (
        <div className="image-preview-overlay" onClick={() => setModalImage(null)}>
          <div className="image-preview-container" onClick={(event) => event.stopPropagation()}>
            <img src={modalImage} alt="Attachment preview" className="image-preview-full" />
            <button
              className="download-btn"
              onClick={async () => {
                try {
                  const result = await saveToGallery(modalImage, { fileName: `note_${Date.now()}.jpg` });
                  alert(formatSaveMessage(result));
                } catch (error) {
                  console.error('Save failed:', error);
                  alert(`Error: ${error.message || JSON.stringify(error)}`);
                }
              }}
            >
              Save
            </button>
            <button className="close-btn" onClick={() => setModalImage(null)}>
              x
            </button>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}
